import React, { useState } from 'react';
import { X, RefreshCw, CheckCircle } from 'lucide-react';
import { Miembro } from '../lib/supabase';
import { DialogShell } from './ui/DialogShell';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';

interface ReingresarMiembroModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  miembro: Miembro | null;
}

export const ReingresarMiembroModal: React.FC<ReingresarMiembroModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  miembro
}) => {
  const [loading, setLoading] = useState(false);

  const resetAndClose = () => {
    onClose();
  };

  const nuevoNumeroIngreso = (miembro?.numero_ingreso ?? 0) + 1;
  const getIngresoLabel = (num: number) => {
    if (num === 1) return 'Primer ingreso';
    if (num === 2) return 'Segundo ingreso';
    if (num === 3) return 'Tercer ingreso';
    return `${num}° ingreso`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await onConfirm();
      onClose();
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos reingresar al miembro.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell
      isOpen={isOpen && !!miembro}
      onClose={resetAndClose}
      onBeforeClose={() => !loading}
      size="sm"
      header={
        <div className="bg-emerald-600 text-white p-6 flex items-center justify-between rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6" />
            <h2 className="text-xl font-bold">Reingresar Miembro</h2>
          </div>
          <button
            onClick={resetAndClose}
            disabled={loading}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-emerald-800 mb-2">
                Estás a punto de reingresar a{' '}
                <span className="font-bold">{miembro?.nombres} {miembro?.apellidos}</span>.
              </p>
              <p className="text-sm text-emerald-700">
                Este será el <span className="font-bold">{getIngresoLabel(nuevoNumeroIngreso)}</span> de este
                miembro en la institución.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-slate-800">Información del reingreso:</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-600">Ingreso actual:</p>
              <p className="font-semibold text-slate-800">{getIngresoLabel(miembro?.numero_ingreso ?? 0)}</p>
            </div>
            <div>
              <p className="text-slate-600">Nuevo ingreso:</p>
              <p className="font-semibold text-emerald-600">{getIngresoLabel(nuevoNumeroIngreso)}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2 text-sm text-slate-700">
          <p className="font-semibold text-slate-800">Al reingresar:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>El miembro volverá a estar activo</li>
            <li>Se incrementará el contador de ingresos</li>
            <li>Se actualizará la fecha de último ingreso</li>
            <li>Aparecerá nuevamente en las listas activas</li>
            <li>Se registrará este cambio en el historial</li>
          </ul>
        </div>


        <div className="flex gap-3">
          <button
            type="button"
            onClick={resetAndClose}
            disabled={loading}
            className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Procesando...' : 'Confirmar Reingreso'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
